import { z } from 'zod';

export const appointmentStatusSchema = z.enum([
  'draft',
  'image_uploaded',
  'ocr_queued',
  'ocr_processing',
  'needs_review',
  'ready',
  'calendar_created',
  'ocr_failed',
]);

export const appointmentFieldSchema = z.enum([
  'title',
  'appointmentDate',
  'startTime',
  'endTime',
  'organization',
  'department',
  'doctorName',
  'location',
  'referenceNumber',
  'phone',
  'instructions',
  'patientName',
  'otherNotes',
]);

const optionalText = z.string().trim().max(2_000).nullish();
const reviewTime = z
  .union([z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/), z.literal(''), z.null()])
  .transform((value) => value || null);

export const appointmentTemplateSchema = z.enum(['meeting', 'task', 'medical', 'activity']);
export type AppointmentTemplate = z.infer<typeof appointmentTemplateSchema>;

const nullableShortText = z.string().trim().max(2_000).nullable();
const nullableLongText = z.string().trim().max(10_000).nullable();
const textList = z.array(z.string().trim().min(1).max(1_000)).max(50).default([]);

export const meetingTemplateDataSchema = z.object({
  templateType: z.literal('meeting'),
  organizer: nullableShortText,
  participants: textList,
  agenda: nullableLongText,
  meetingLink: z.string().trim().max(2_000).nullable(),
  materials: textList,
  relatedTaskTitle: nullableShortText,
  relatedTaskDueDate: z.string().date().nullable(),
  referenceNumber: z.string().trim().max(200).nullable(),
});

export const taskTemplateDataSchema = z.object({
  templateType: z.literal('task'),
  summaryPoints: textList,
  assignedBy: nullableShortText,
  contact: nullableShortText,
  organization: nullableShortText,
  location: nullableShortText,
  referenceNumber: z.string().trim().max(200).nullable(),
  priority: z.enum(['low', 'normal', 'high']).default('normal'),
  relatedMeetingDate: z.string().date().nullable(),
});

export const medicalTemplateDataSchema = z.object({
  templateType: z.literal('medical'),
  serviceType: nullableShortText,
  preparationItems: textList,
  organization: nullableShortText,
  department: nullableShortText,
  doctorName: nullableShortText,
  patientName: z.string().trim().max(500).nullable(),
  location: nullableShortText,
  referenceNumber: z.string().trim().max(200).nullable(),
  phone: z.string().trim().max(100).nullable(),
  instructions: nullableLongText,
  otherNotes: nullableLongText,
});

export const activityTemplateDataSchema = z.object({
  templateType: z.literal('activity'),
  activityType: nullableShortText,
  dressCode: nullableShortText,
  golfCourse: nullableShortText.optional(),
  teeOffTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .nullable()
    .optional(),
  golfGroupId: z.string().uuid().nullable().optional(),
  golfGroupName: nullableShortText.optional(),
  golfers: textList.optional(),
  price: nullableShortText.optional(),
  bookedBy: nullableShortText.optional(),
  golfStartMode: z.enum(['tee_time', 'shotgun']).nullable().optional(),
  golfStartHole: nullableShortText.optional(),
});

export const appointmentTemplateDataSchema = z.discriminatedUnion('templateType', [
  meetingTemplateDataSchema,
  taskTemplateDataSchema,
  medicalTemplateDataSchema,
  activityTemplateDataSchema,
]);
export type AppointmentTemplateData = z.infer<typeof appointmentTemplateDataSchema>;

export const aiFieldSuggestionSchema = z.object({
  value: z.string().trim().min(1).max(10_000),
  confidence: z.number().min(0).max(1),
  sourceText: z.string().trim().min(1).max(2_000),
  originalValue: z.string().trim().min(1).max(2_000).optional(),
  origin: z.enum(['current_document', 'history']).default('current_document'),
});
export type AiFieldSuggestion = z.infer<typeof aiFieldSuggestionSchema>;
export const aiFieldSuggestionsSchema = z
  .record(z.string().trim().min(1).max(100), z.array(aiFieldSuggestionSchema).max(3))
  .default({});

export const extractedAppointmentSchema = z.object({
  title: z.string().trim().max(300).nullish(),
  appointmentDate: z.string().date().nullish(),
  startTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .nullish(),
  endTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .nullish(),
  organization: optionalText,
  department: optionalText,
  doctorName: optionalText,
  location: optionalText,
  referenceNumber: z.string().trim().max(200).nullish(),
  phone: z.string().trim().max(100).nullish(),
  instructions: z.string().trim().max(10_000).nullish(),
  patientName: z.string().trim().max(500).nullish(),
  otherNotes: z.string().trim().max(10_000).nullish(),
  templateData: appointmentTemplateDataSchema.optional(),
  fieldSuggestions: aiFieldSuggestionsSchema.optional(),
});

export type ExtractedAppointment = z.infer<typeof extractedAppointmentSchema>;

export const confidenceSchema = z.partialRecord(appointmentFieldSchema, z.number().min(0).max(1));

export const validatedOcrResultSchema = z.object({
  rawText: z.string().max(200_000),
  extracted: extractedAppointmentSchema,
  items: z.array(extractedAppointmentSchema).min(1).max(20).optional(),
  confidence: confidenceSchema,
  warnings: z.array(z.string().max(500)).max(50).default([]),
  providerMetadata: z.record(z.string(), z.unknown()).default({}),
});

export type ValidatedOcrResult = z.infer<typeof validatedOcrResultSchema>;

export const createAppointmentSchema = z.object({
  timezone: z.string().trim().min(1).max(100).default('Asia/Bangkok'),
  templateType: appointmentTemplateSchema.default('medical'),
});

export const appointmentPatchSchema = z
  .object({
    templateType: appointmentTemplateSchema.default('medical'),
    title: z.string().trim().min(1).max(300),
    appointmentDate: z.string().date(),
    endDate: z.string().date().optional(),
    startTime: reviewTime,
    endTime: reviewTime,
    timezone: z.string().trim().min(1).max(100),
    organization: z.string().trim().max(2_000).nullable(),
    department: z.string().trim().max(2_000).nullable(),
    doctorName: z.string().trim().max(2_000).nullable(),
    location: z.string().trim().max(2_000).nullable(),
    referenceNumber: z.string().trim().max(200).nullable(),
    phone: z.string().trim().max(100).nullable(),
    instructions: z.string().trim().max(10_000).nullable(),
    patientName: z.string().trim().max(500).nullable(),
    otherNotes: z.string().trim().max(10_000).nullable(),
    templateData: appointmentTemplateDataSchema.optional(),
    meetingTopicTitle: z.string().trim().max(200).optional(),
    expectedUpdatedAt: z.string().datetime().optional(),
  })
  .superRefine((value, context) => {
    if (value.templateData && value.templateData.templateType !== value.templateType) {
      context.addIssue({
        code: 'custom',
        path: ['templateData'],
        message: 'ข้อมูลไม่ตรงกับประเภทเอกสาร',
      });
    }
    if (!value.startTime && value.endTime) {
      context.addIssue({
        code: 'custom',
        path: ['endTime'],
        message: 'ไม่สามารถมีเวลาสิ้นสุดโดยไม่มีเวลาเริ่ม',
      });
    }
    if (Boolean(value.startTime) !== Boolean(value.endTime)) {
      context.addIssue({
        code: 'custom',
        path: ['startTime'],
        message: 'กรุณาระบุเวลาเริ่มและเวลาสิ้นสุด',
      });
      return;
    }
    if (!value.startTime && !value.endTime) return;
    const start = Date.parse(`${value.appointmentDate}T${value.startTime}:00+07:00`);
    const end = Date.parse(`${value.endDate ?? value.appointmentDate}T${value.endTime}:00+07:00`);
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
      context.addIssue({
        code: 'custom',
        path: ['endTime'],
        message: 'เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่มต้น',
      });
    }
  });

export type AppointmentPatch = z.infer<typeof appointmentPatchSchema>;
export type AppointmentPatchInput = z.input<typeof appointmentPatchSchema>;

export const reminderSchema = z.object({
  method: z.literal('popup'),
  minutes: z.number().int().min(0).max(40_320),
});

export const createCalendarEventSchema = z.object({
  calendarId: z.string().trim().min(1).max(1_024).default('primary'),
  reminders: z
    .array(reminderSchema)
    .min(1)
    .max(5)
    .default([
      { method: 'popup', minutes: 1_440 },
      { method: 'popup', minutes: 120 },
    ]),
  forceDuplicate: z.boolean().default(false),
});

export const calendarImportQuerySchema = z.object({
  calendarId: z.string().trim().min(1).max(1_024),
  timeMin: z.string().datetime({ offset: true }),
  timeMax: z.string().datetime({ offset: true }),
});

export const calendarImportItemSchema = z.object({
  sourceEventId: z.string().trim().min(1).max(512),
  sourceUpdatedAt: z.string().datetime({ offset: true }).nullish(),
  calendarId: z.string().trim().min(1).max(1_024),
  title: z.string().trim().min(1).max(300),
  templateType: appointmentTemplateSchema,
  meetingTopicTitle: z.string().trim().max(200).optional(),
  appointmentDate: z.string().date(),
  endDate: z.string().date().optional(),
  startTime: reviewTime,
  endTime: reviewTime,
  timezone: z.string().trim().min(1).max(100).default('Asia/Bangkok'),
  location: z.string().trim().max(2_000).nullish(),
  description: z.string().trim().max(10_000).nullish(),
  eventUrl: z.string().url().nullish(),
  attachments: z
    .array(
      z.object({
        title: z.string().trim().max(500),
        fileUrl: z.string().url(),
        mimeType: z.string().trim().max(200).nullish(),
      }),
    )
    .max(20)
    .default([]),
  existingAppointmentId: z.string().uuid().nullish(),
  decision: z.enum(['import', 'update', 'skip']).default('import'),
});

export const calendarImportCommitSchema = z.object({
  items: z.array(calendarImportItemSchema).min(1).max(100),
});

export const idParamSchema = z.object({ id: z.string().uuid() });
export const groupSelectionSchema = z.object({
  items: z
    .array(z.object({ id: z.string().uuid(), selected: z.boolean() }))
    .min(1)
    .max(20),
});

export const listAppointmentsQuerySchema = z.object({
  cursor: z.string().datetime().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: appointmentStatusSchema.optional(),
});

const appointmentOcrQueueMessageSchema = z.object({
  kind: z.literal('appointment').optional(),
  appointmentId: z.string().uuid(),
  userId: z.string().uuid(),
  requestedAt: z.string().datetime(),
  traceId: z.string().uuid(),
});

const libraryDocumentOcrQueueMessageSchema = z.object({
  kind: z.literal('library_document'),
  documentId: z.string().uuid(),
  userId: z.string().uuid(),
  requestedAt: z.string().datetime(),
  traceId: z.string().uuid(),
});

const libraryDocumentFileOcrQueueMessageSchema = z.object({
  kind: z.literal('library_document_file'),
  documentId: z.string().uuid(),
  documentFileId: z.string().uuid(),
  ocrRunId: z.string().uuid(),
  userId: z.string().uuid(),
  requestedAt: z.string().datetime(),
  traceId: z.string().uuid(),
});

export const ocrQueueMessageSchema = z.union([
  appointmentOcrQueueMessageSchema,
  libraryDocumentOcrQueueMessageSchema,
  libraryDocumentFileOcrQueueMessageSchema,
]);

export type OcrQueueMessage = z.infer<typeof ocrQueueMessageSchema>;

export const turnstileRequestSchema = z.object({
  turnstileToken: z.string().min(1).max(2_048),
});

export const imageUploadMetadataSchema = z.object({
  clientSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/i)
    .optional(),
});

export const documentPageParamSchema = z.object({
  id: z.string().uuid(),
  page: z.coerce.number().int().min(1).max(20),
});
export const documentPageUploadSchema = z.object({
  totalPages: z.coerce.number().int().min(1).max(20),
  sourcePages: z.coerce.number().int().min(1).max(10_000),
});

export const storedFilePurposeSchema = z.enum([
  'meeting_attachment',
  'meeting_transcript',
  'meeting_summary',
  'document_original',
]);

export const storedFileCategorySchema = z.enum([
  'meeting_report',
  'audio_recording',
  'transcript',
  'summary',
  'attachment',
  'preparation',
  'completed_work',
  'other',
]);

export const updateStoredFileSchema = z
  .object({
    fileName: z.string().trim().min(1).max(255).optional(),
    category: storedFileCategorySchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, 'ต้องระบุข้อมูลไฟล์ที่ต้องการแก้ไข');

export const storedFileEntityTypeSchema = z.enum(['meeting', 'topic', 'appointment', 'document']);

export const documentTaxonomySchema = z.object({
  name: z.string().trim().min(1).max(100),
  color: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i)
    .default('#0f766e'),
});

export const updateDocumentTaxonomySchema = documentTaxonomySchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'ต้องระบุข้อมูลที่ต้องการแก้ไข');

export const documentStatusSchema = z.enum([
  'draft',
  'ocr_queued',
  'ocr_processing',
  'needs_review',
  'ready',
  'ocr_failed',
]);

export const createLibraryDocumentSchema = z.object({
  storedFileId: z.string().uuid(),
  title: z.string().trim().min(1).max(255),
  documentNumber: z.string().trim().max(150).nullish(),
  documentDate: z.string().date().nullish(),
  categoryId: z.string().uuid().nullish(),
  agencyId: z.string().uuid().nullish(),
  labelIds: z.array(z.string().uuid()).max(30).default([]),
  duplicateMode: z.enum(['reuse', 'new_version']).default('reuse'),
});

export const updateLibraryDocumentSchema = z
  .object({
    title: z.string().trim().min(1).max(255).optional(),
    documentNumber: z.string().trim().max(150).nullable().optional(),
    documentDate: z.string().date().nullable().optional(),
    categoryId: z.string().uuid().nullable().optional(),
    agencyId: z.string().uuid().nullable().optional(),
    labelIds: z.array(z.string().uuid()).max(30).optional(),
    status: z.enum(['needs_review', 'ready']).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, 'ต้องระบุข้อมูลที่ต้องการแก้ไข');

export const listLibraryDocumentsQuerySchema = z.object({
  query: z.string().trim().max(200).optional(),
  categoryId: z.string().uuid().optional(),
  agencyId: z.string().uuid().optional(),
  labelId: z.string().uuid().optional(),
  labelIds: z.string().trim().max(1_200).optional(),
  status: documentStatusSchema.optional(),
  legalStatus: z.enum(['active', 'amended', 'partially_repealed', 'repealed']).optional(),
  relationshipType: z
    .enum([
      'replaces',
      'repeals_all',
      'repeals_part',
      'amends',
      'extends',
      'references',
      'issued_under',
    ])
    .optional(),
  relationshipDirection: z.enum(['any', 'incoming', 'outgoing', 'unlinked']).optional(),
  includeProposed: z.coerce.boolean().default(false),
  dateFrom: z.string().date().optional(),
  dateTo: z.string().date().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});

export const libraryDocumentOcrSchema = z.object({
  nativeText: z.string().max(2_000_000).optional(),
  sourcePages: z.number().int().min(1).max(10_000),
});

export const libraryDocumentLinkSchema = z.object({
  entityType: z.enum(['meeting', 'appointment', 'topic']),
  entityId: z.string().uuid(),
});

export const documentRelationshipTypeSchema = z.enum([
  'replaces',
  'repeals_all',
  'repeals_part',
  'amends',
  'extends',
  'references',
  'issued_under',
]);

export const createDocumentRelationshipSchema = z.object({
  targetDocumentId: z.string().uuid(),
  relationshipType: documentRelationshipTypeSchema,
  scopeText: z.string().trim().max(2_000).nullish(),
  effectiveDate: z.string().date().nullish(),
  evidenceText: z.string().trim().max(4_000).nullish(),
  evidencePage: z.number().int().min(1).max(10_000).nullish(),
  status: z.enum(['proposed', 'confirmed']).default('confirmed'),
});

export const updateDocumentRelationshipSchema = z
  .object({
    relationshipType: documentRelationshipTypeSchema.optional(),
    scopeText: z.string().trim().max(2_000).nullable().optional(),
    effectiveDate: z.string().date().nullable().optional(),
    evidenceText: z.string().trim().max(4_000).nullable().optional(),
    evidencePage: z.number().int().min(1).max(10_000).nullable().optional(),
    status: z.enum(['proposed', 'confirmed', 'rejected']).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, 'ต้องระบุข้อมูลที่ต้องการแก้ไข');

export const libraryAiRequestSchema = z.object({
  prompt: z.string().trim().min(1).max(2_000),
  mode: z.enum(['summary', 'question']),
});

export const initiateStoredFileUploadSchema = z
  .object({
    fileName: z.string().trim().min(1).max(255),
    contentType: z.string().trim().min(1).max(200),
    sizeBytes: z.number().int().positive().max(5_368_709_120),
    sha256: z
      .string()
      .regex(/^[a-f0-9]{64}$/i)
      .transform((value) => value.toLowerCase()),
    purpose: storedFilePurposeSchema,
    category: storedFileCategorySchema.optional(),
    linkedEntityType: storedFileEntityTypeSchema.nullish(),
    linkedEntityId: z.string().uuid().nullish(),
  })
  .superRefine((value, context) => {
    if (Boolean(value.linkedEntityType) !== Boolean(value.linkedEntityId)) {
      context.addIssue({
        code: 'custom',
        path: ['linkedEntityId'],
        message: 'ต้องระบุประเภทและรหัสข้อมูลที่เชื่อมโยงให้ครบ',
      });
    }
  });

export const storedFilePartParamSchema = z.object({
  id: z.string().uuid(),
  part: z.coerce.number().int().min(1).max(10_000),
});

export const listStoredFilesQuerySchema = z
  .object({
    linkedEntityType: storedFileEntityTypeSchema.optional(),
    linkedEntityId: z.string().uuid().optional(),
    purpose: storedFilePurposeSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .superRefine((value, context) => {
    if (Boolean(value.linkedEntityType) !== Boolean(value.linkedEntityId)) {
      context.addIssue({
        code: 'custom',
        path: ['linkedEntityId'],
        message: 'ต้องระบุประเภทและรหัสข้อมูลที่เชื่อมโยงให้ครบ',
      });
    }
  });

export const meetingTopicStatusSchema = z.enum(['active', 'archived']);
export const meetingStatusSchema = z.enum(['draft', 'planned', 'completed', 'cancelled']);
export const meetingActionStatusSchema = z.enum(['open', 'in_progress', 'done', 'cancelled']);
export const meetingDocumentTypeSchema = z.enum(['attachment', 'transcript', 'summary']);
export const meetingDocumentStatusSchema = z.enum([
  'uploaded',
  'processing',
  'needs_review',
  'confirmed',
  'failed',
]);

const meetingColorSchema = z.string().regex(/^#[0-9a-f]{6}$/i, 'สีต้องอยู่ในรูปแบบ #RRGGBB');
const nullableIsoDateTimeSchema = z.string().datetime({ offset: true }).nullable();

export const createMeetingTopicSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(4_000).nullish(),
  color: meetingColorSchema.default('#0f766e'),
});

export const updateMeetingTopicSchema = createMeetingTopicSchema
  .partial()
  .extend({ status: meetingTopicStatusSchema.optional() })
  .refine((value) => Object.keys(value).length > 0, 'ต้องระบุข้อมูลที่ต้องการแก้ไข');

export const listMeetingTopicsQuerySchema = z.object({
  query: z.string().trim().max(200).optional(),
  status: meetingTopicStatusSchema.optional(),
  labelId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const createMeetingSchema = z.object({
  topicId: z.string().uuid(),
  previousMeetingId: z.string().uuid().nullish(),
  appointmentId: z.string().uuid().nullish(),
  title: z.string().trim().min(1).max(300),
  heldAt: nullableIsoDateTimeSchema.optional(),
  location: z.string().trim().max(1_000).nullish(),
  organization: z.string().trim().max(500).nullish(),
  summary: z.string().trim().max(40_000).nullish(),
  decisions: z.string().trim().max(40_000).nullish(),
  notes: z.string().trim().max(40_000).nullish(),
  status: meetingStatusSchema.default('draft'),
  labelIds: z.array(z.string().uuid()).max(20).default([]),
});

export const updateMeetingSchema = createMeetingSchema
  .omit({ topicId: true })
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'ต้องระบุข้อมูลที่ต้องการแก้ไข');

export const createMeetingLabelSchema = z.object({
  name: z.string().trim().min(1).max(80),
  color: meetingColorSchema.default('#475569'),
});
export const updateMeetingLabelSchema = createMeetingLabelSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'ต้องระบุข้อมูลที่ต้องการแก้ไข');

export const createMeetingActionSchema = z.object({
  title: z.string().trim().min(1).max(300),
  details: z.string().trim().max(10_000).nullish(),
  primaryOwnerName: z.string().trim().max(200).nullish(),
  status: meetingActionStatusSchema.default('open'),
});

export const updateMeetingActionSchema = createMeetingActionSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'ต้องระบุข้อมูลที่ต้องการแก้ไข');

export const deleteMeetingSchema = z.object({
  treeAction: z.enum(['reparent', 'orphan']),
  confirm: z.literal(true),
});

export const createMeetingDocumentSchema = z.object({
  storedFileId: z.string().uuid().nullish(),
  documentType: meetingDocumentTypeSchema,
  extractedText: z.string().max(500_000).nullish(),
  summaryText: z.string().max(100_000).nullish(),
  processingStatus: meetingDocumentStatusSchema.default('uploaded'),
  aiSuggestions: z.record(z.string(), z.unknown()).default({}),
});

export const meetingSearchQuerySchema = z.object({
  query: z.string().trim().min(2).max(200),
  includeTranscript: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  topicId: z.string().uuid().optional(),
  labelId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

export const aiSearchScopeSchema = z.enum(['meetings', 'documents', 'transcripts', 'actions']);
export const aiSearchEstimateSchema = z.object({
  query: z.string().trim().min(2).max(500),
  scopes: z.array(aiSearchScopeSchema).min(1).max(4),
  dateFrom: z.string().date().optional(),
  dateTo: z.string().date().optional(),
  selectedSourceIds: z.array(z.string().uuid()).max(50).default([]),
  maxSources: z.number().int().min(1).max(50).default(20),
  responseStyle: z.enum(['short', 'standard', 'detailed']).default('standard'),
});
export const aiSearchAskSchema = aiSearchEstimateSchema.extend({
  confirmed: z.literal(true),
});

export const updateAppointmentPromptSchema = z.object({
  templateType: appointmentTemplateSchema,
  prompt: z.string().trim().min(20).max(20_000),
});

export const listPlaudRecordingsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(1_000).default(1),
  pageSize: z.coerce.number().int().min(10).max(100).default(20),
});

export const importPlaudRecordingSchema = z.object({
  recordingId: z.string().regex(/^[A-Za-z0-9_-]{1,128}$/),
});

export const confirmPlaudSummarySchema = z.object({
  importId: z.string().uuid(),
});

export const mergeMeetingSummarySchema = z.object({
  importIds: z.array(z.string().uuid()).min(2).max(20),
  prompt: z.string().trim().min(20).max(20_000),
});

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
    traceId: z.string().optional(),
  }),
});

export type ApiError = z.infer<typeof apiErrorSchema>;
